import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, finalize, Observable, shareReplay, switchMap, throwError } from 'rxjs';

import { AuthService } from '@core/auth/auth.service';
import { EnvironmentService } from '@core/config/environment.service';
import { AuthEventsService } from '@core/services/auth-events.service';
import { TokenStorageService } from '@core/services/token-storage.service';

import type { AuthTokens } from '@core/auth/auth.models';

const RETRY_ONCE = new HttpContextToken<boolean>(() => false);

@Injectable({ providedIn: 'root' })
export class AuthRefreshCoordinatorService {
  private refreshInFlight$: Observable<AuthTokens> | null = null;

  getRefreshStream(authService: AuthService): Observable<AuthTokens> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = authService.refreshSession().pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
      );
    }
    return this.refreshInFlight$;
  }
}

function normalizePath(path: string): string {
  const base = path.startsWith('/') ? path : `/${path}`;
  return base.replace(/\/+$/, '');
}

function pathnameFromUrl(url: string): string {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    return new URL(url, base).pathname;
  } catch {
    const rawPath = url.split('?')[0] ?? url;
    return rawPath.split('#')[0] ?? rawPath;
  }
}

function isAuthPublicPath(pathname: string, env: EnvironmentService): boolean {
  // Keep refresh/login public: refresh flows may run after access-token expiry.
  const endpoints = [
    env.auth.endpoints.login,
    env.auth.endpoints.refresh,
    env.auth.endpoints.forgotPassword,
  ].filter((endpoint): endpoint is string => !!endpoint);

  return endpoints.some((endpoint) => pathname.endsWith(normalizePath(endpoint)));
}

function shouldSkipBearer(url: string, env: EnvironmentService): boolean {
  const pathname = pathnameFromUrl(url);
  if (isAuthPublicPath(pathname, env)) {
    return true;
  }
  return env.auth.publicUrlPathPrefixes.some((prefix) =>
    pathname.startsWith(normalizePath(prefix)),
  );
}

function isRefreshRequest(url: string, env: EnvironmentService): boolean {
  return pathnameFromUrl(url).endsWith(normalizePath(env.auth.endpoints.refresh));
}

/** Attaches `Authorization: Bearer` when a token exists (aligned with remmi-v16 auth interceptor). */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const env = inject(EnvironmentService);
  const tokens = inject(TokenStorageService);
  const authService = inject(AuthService);
  const authEvents = inject(AuthEventsService);
  const refreshCoordinator = inject(AuthRefreshCoordinatorService);

  const skipBearer = shouldSkipBearer(req.url, env);
  const token = tokens.getAccessToken();
  const request = skipBearer || !token
    ? req
    : req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });

  return next(request).pipe(
    catchError((error: unknown) => {
      if (
        skipBearer ||
        isRefreshRequest(req.url, env) ||
        !token ||
        req.context.get(RETRY_ONCE) ||
        !(error instanceof HttpErrorResponse) ||
        error.status !== 401
      ) {
        return throwError(() => error);
      }

      const canTryRefresh =
        env.auth.refreshTokenTransport === 'httpOnlyCookie' || !!tokens.getRefreshToken();
      if (!canTryRefresh) {
        authEvents.emitUnauthorized();
        return throwError(() => error);
      }

      return refreshCoordinator.getRefreshStream(authService).pipe(
        switchMap(() => {
          const nextToken = tokens.getAccessToken();
          if (!nextToken) {
            authEvents.emitUnauthorized();
            return throwError(() => error);
          }
          return next(
            req.clone({
              context: req.context.set(RETRY_ONCE, true),
              setHeaders: { Authorization: `Bearer ${nextToken}` },
            }),
          );
        }),
        catchError((refreshError: unknown) => {
          authEvents.emitUnauthorized();
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
